import { useState, useEffect } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { useTranslation } from 'react-i18next';
import { useContracts } from '@/hooks/useContracts';
// Payment schedule is generated from deal.metadata.payment_plan_config
import { useSuvSign } from '@/hooks/useSuvSign';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Plus, Eye, Download, Send, CheckCircle, XCircle, Clock, AlertCircle, Upload, Ban } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { format, addWeeks, addMonths } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import { suvSignService } from '@/services/suvSignService';

// Helper: check if a contract is "cancelled locally" (completed but voided locally)
function isCancelledLocally(contract: any): boolean {
  return contract.status === 'completed' && (contract.metadata as any)?.cancelled_locally === true;
}

// Hook to check if deal has active (non-void, non-declined) contract
export function useHasActiveContract(dealId?: string) {
  const { contracts, loading, loadContracts } = useContracts(dealId);
  const hasActiveContract = contracts.some(c => 
    ['draft', 'sent', 'viewed', 'completed'].includes(c.status) && !isCancelledLocally(c)
  );
  return { hasActiveContract, loading, refresh: loadContracts };
}

interface ContractsTabProps {
  dealId: string;
  contact?: any;
  deal?: any;
  finalTotal?: number;
  onContractChange?: () => void;
}

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800 border-gray-200',
  sent: 'bg-blue-100 text-blue-800 border-blue-200',
  viewed: 'bg-purple-100 text-purple-800 border-purple-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  declined: 'bg-red-100 text-red-800 border-red-200',
  expired: 'bg-orange-100 text-orange-800 border-orange-200',
  void: 'bg-gray-100 text-gray-800 border-gray-200',
  issues: 'bg-yellow-100 text-yellow-800 border-yellow-200',
};

const statusIcons: Record<string, any> = {
  draft: FileText,
  sent: Send,
  viewed: Eye,
  completed: CheckCircle,
  declined: XCircle,
  expired: AlertCircle,
  void: XCircle,
  issues: AlertCircle,
};

// Gera schedule de datas baseado no config do payment_plan_config (aba Detalhes)
function generateScheduleFromConfig(config: any, totalAmount: number) {
  if (!config) return { deposits: [] as { amount: number; date: Date }[], installments: [] as { amount: number; date: Date }[] };

  const deposits: { amount: number; date: Date }[] = [];
  const installments: { amount: number; date: Date }[] = [];

  const addInterval = (startDate: Date, frequency: string, index: number): Date => {
    switch (frequency) {
      case 'weekly': return addWeeks(startDate, index);
      case 'biweekly': return addWeeks(startDate, index * 2);
      case 'monthly': return addMonths(startDate, index);
      default: return addMonths(startDate, index);
    }
  };

  // Deposits
  const depositAmount = config.depositAmount || 0;
  const depositSplit = config.depositSplit;
  if (depositAmount > 0 && depositSplit) {
    const numDeposits = depositSplit.number_of_payments || 1;
    const startDate = depositSplit.start_date ? new Date(depositSplit.start_date + 'T12:00:00') : new Date();
    const customAmounts = config.depositCustomAmounts || [];
    const customDates = config.depositCustomDates || [];
    const perDeposit = depositAmount / numDeposits;

    for (let i = 0; i < numDeposits; i++) {
      const amount = customAmounts[i] || perDeposit;
      const date = customDates[i] ? new Date(customDates[i] + 'T12:00:00') : addInterval(startDate, depositSplit.frequency || 'monthly', i);
      deposits.push({ amount, date });
    }
  }

  // Installments
  const installmentSplit = config.installmentSplit;
  if (installmentSplit) {
    const installmentTotal = totalAmount - (depositAmount || 0);
    const numInstallments = installmentSplit.number_of_payments || 1;
    const startDate = installmentSplit.start_date ? new Date(installmentSplit.start_date + 'T12:00:00') : new Date();
    const customAmounts = config.installmentCustomAmounts || [];
    const customDates = config.installmentCustomDates || [];
    const perInstallment = installmentTotal / numInstallments;

    for (let i = 0; i < numInstallments; i++) {
      const amount = customAmounts[i] || perInstallment;
      const date = customDates[i] ? new Date(customDates[i] + 'T12:00:00') : addInterval(startDate, installmentSplit.frequency || 'monthly', i);
      installments.push({ amount, date });
    }
  }

  return { deposits, installments };
}

export function ContractsTab({ dealId, contact, deal, finalTotal, onContractChange }: ContractsTabProps) {
  const { t } = useTranslation();
  const { contracts, loading, createContract, updateContract, deleteContract, loadContracts } = useContracts(dealId);
  const { 
    sendToSuvSign, 
    loading: suvSignLoading, 
    config: suvSignConfig, 
    loadConfig: loadSuvSignConfig
  } = useSuvSign();
  const { role } = useUserRole();
  const isAdmin = role === 'admin';
  
  // Check if deal is WON → read-only mode
  const isDealWon = deal?.stage?.toLowerCase() === 'won';
  
  // Check if there's an active contract (blocks creating new ones)
  const hasActiveContract = contracts.some(c => 
    ['draft', 'sent', 'viewed', 'completed'].includes(c.status) && !isCancelledLocally(c)
  );
  
  const [isSendDialogOpen, setIsSendDialogOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState<any>(null);
  const [sendFormData, setSendFormData] = useState({
    recipient_email: '',
    recipient_name: '',
  });
  
  // Unified dismiss confirmation state
  const [contractToDismiss, setContractToDismiss] = useState<any>(null);
  const [dismissing, setDismissing] = useState(false);
  
  // Redirect confirmation modal state
  const [showRedirectConfirm, setShowRedirectConfirm] = useState(false);
  const [pendingRedirectData, setPendingRedirectData] = useState<{ contractData: any; url: string } | null>(null);
  
  // Admin upload states
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadFormData, setUploadFormData] = useState({
    contract_name: '',
    status: 'draft' as 'draft' | 'sent' | 'completed' | 'issues',
    notes: '',
  });

  useEffect(() => {
    loadSuvSignConfig();
  }, []);

  // Polling: watch draft contracts for status changes (webhook may update them)
  useEffect(() => {
    const draftContracts = contracts.filter(c => c.status === 'draft');
    if (draftContracts.length === 0) return;

    const interval = setInterval(async () => {
      for (const draft of draftContracts) {
        const { data } = await supabase
          .from('deal_contracts')
          .select('status')
          .eq('id', draft.id)
          .single();
        
        if (data && data.status !== 'draft') {
          loadContracts();
          onContractChange?.();
          clearInterval(interval);
          return;
        }
      }
    }, 5000);

    const timeout = setTimeout(() => clearInterval(interval), 120000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [contracts, dealId]);

  useEffect(() => {
    if (contact) {
      setSendFormData({
        recipient_email: contact.primary_email || '',
        recipient_name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim(),
      });
    }
  }, [contact]);
  
  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadFormData({
      contract_name: '',
      status: 'draft',
      notes: '',
    });
  };

  const handleUploadContract = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile) return;

    setUploading(true);
    try {
      // 1. Upload file to storage - sanitize filename to avoid InvalidKey error
      const sanitizedFileName = uploadFile.name
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9._-]/g, '_')
        .replace(/_+/g, '_');
      const fileName = `contracts/${dealId}/${Date.now()}_${sanitizedFileName}`;
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(fileName, uploadFile);

      if (uploadError) throw uploadError;

      // 2. Create contract record (save storage path, not public URL)
      await createContract({
        deal_id: dealId,
        contract_name: uploadFormData.contract_name || uploadFile.name,
        contract_type: 'manual',
        status: uploadFormData.status,
        contract_url: fileName, // Save only the storage path
        notes: uploadFormData.notes,
        signed_at: uploadFormData.status === 'completed' ? new Date().toISOString() : null,
        sent_at: uploadFormData.status === 'sent' || uploadFormData.status === 'completed' ? new Date().toISOString() : null,
      });

      toast.success(t('contracts.uploadSuccess'));
      setIsUploadDialogOpen(false);
      resetUploadForm();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(t('contracts.uploadError'));
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateStatus = async (contractId: string, newStatus: string) => {
    try {
      const updates: any = { status: newStatus };

      if (newStatus === 'completed') {
        updates.signed_at = new Date().toISOString();
      }
      if (newStatus === 'sent' && !updates.sent_at) {
        updates.sent_at = new Date().toISOString();
      }

      await updateContract(contractId, updates);
      toast.success(t('contracts.statusUpdated'));
    } catch (error) {
      toast.error(t('contracts.statusUpdateError'));
    }
  };

  // Download handler using signed URLs for private bucket
  const handleDownload = async (contract: any) => {
    if (!contract.contract_url) return;
    
    try {
      let storagePath = contract.contract_url;
      
      // If it's a full URL (legacy), extract the storage path
      if (contract.contract_url.startsWith('http')) {
        const match = contract.contract_url.match(/\/documents\/(.+)$/);
        if (match) {
          storagePath = match[1];
        } else {
          // If we can't extract, try opening directly (might work for truly public URLs)
          window.open(contract.contract_url, '_blank');
          return;
        }
      }
      
      // Generate signed URL (valid for 60 seconds)
      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(storagePath, 60);
      
      if (error) throw error;
      
      window.open(data.signedUrl, '_blank');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(t('contracts.downloadError') || 'Erro ao baixar contrato');
    }
  };

  const handleCreateContract = async () => {
    console.log("=== DEBUG handleCreateContract ===");
    console.log("contact:", contact);
    console.log("deal metadata:", deal?.metadata);
    
    if (!contact) {
      toast.error(t('contracts.clientNotLoaded'));
      return;
    }
    
    if (!contact.first_name && !contact.last_name) {
      toast.error(t('contracts.clientNoName'));
      return;
    }

    // Gerar schedule do payment_plan_config (aba Detalhes)
    const paymentConfig = deal?.metadata?.payment_plan_config;
    if (!paymentConfig) {
      toast.error('Nenhuma configuração de pagamento encontrada. Configure o plano de pagamento na aba Detalhes.');
      return;
    }

    const totalAmount = finalTotal || deal?.total_amount || deal?.amount || 0;
    const { deposits, installments } = generateScheduleFromConfig(paymentConfig, totalAmount);

    console.log("Payment config:", paymentConfig);
    console.log("Generated deposits:", deposits);
    console.log("Generated installments:", installments);
    
    const templateId = "5a8fad1d-aa0c-4ee6-b9a1-ae985958789c";
    
    const firstName = contact?.first_name || '';
    const lastName = contact?.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    
    const primaryAddress = contact?.contact_addresses?.find((a: any) => a.is_primary) 
      || contact?.contact_addresses?.[0] 
      || null;

    const contractName = `Contrato - ${fullName}`;
    
    const data = {
      client: {
        firstName,
        lastName,
        email: contact?.primary_email || '',
        phone: contact?.primary_phone || '',
        company: '',
        name: fullName,
        street: primaryAddress?.line1 || '',
        city: primaryAddress?.city || '',
        state: primaryAddress?.state || '',
        zip: primaryAddress?.postal_code || '',
        country: primaryAddress?.country_code || ''
      },
      deal: {
        name: deal?.name || '',
        closingDate: deal?.closed_date || deal?.expected_close_date || '',
        amount: totalAmount.toString(),
        stage: deal?.stage || '',
        probability: deal?.probability?.toString() || '',
        description: deal?.description || deal?.notes || '',
      },
      deal_id: dealId,
      contract_name: contractName,
      Payment: [...deposits, ...installments].map((p, idx) => ({
        Index: String(idx + 1),
        Amount: '$' + p.amount.toFixed(2).replace('.', ','),
        Date: safeFormat(p.date, 'MM/dd/yyyy'),
      })),
    };
    
    console.log("Data a ser enviada para SuvSign:", data);

    const encodedData = encodeURIComponent(JSON.stringify(data));
    const url = `https://suvsign.com/create-from-template?template_id=${templateId}&data=${encodedData}`;

    // Preparar dados e abrir modal de confirmação
    setPendingRedirectData({
      contractData: {
        deal_id: dealId,
        contract_name: contractName,
        contract_type: 'service_agreement',
        status: 'draft',
        integration_provider: 'suvsign',
        metadata: {
          deal_id: dealId,
          contract_name: contractName,
          client: data.client,
          deal: data.deal,
          source: 'template',
        },
      },
      url,
    });
    setShowRedirectConfirm(true);
  };

  const handleConfirmCreateContract = async () => {
    if (!pendingRedirectData) return;
    
    // Anti-popup: abrir janela ANTES do await
    const win = window.open('', '_blank');
    
    try {
      await createContract(pendingRedirectData.contractData);
      console.log("✅ Registro local criado em deal_contracts");
      onContractChange?.();
      
      if (win) {
        win.location.href = pendingRedirectData.url;
      }
    } catch (err) {
      console.error("❌ Erro ao criar registro local:", err);
      toast.error('Erro ao criar contrato local. Tente novamente.');
      if (win) win.close();
    } finally {
      setShowRedirectConfirm(false);
      setPendingRedirectData(null);
    }
  };

  // Unified dismiss handler: tries cancel on SuvSign, falls back to local delete/cancel
  const handleDismissContract = async (contract: any) => {
    setDismissing(true);
    try {
      const { data: freshContract, error: fetchError } = await supabase
        .from('deal_contracts')
        .select('*')
        .eq('id', contract.id)
        .single();

      if (fetchError || !freshContract) {
        toast.error('Erro ao buscar contrato atualizado.');
        return;
      }

      const hasSuvSignId = !!freshContract.suvsign_document_id;

      if (!hasSuvSignId) {
        await deleteContract(freshContract.id);
        toast.success('Contrato excluído com sucesso.');
        onContractChange?.();
        return;
      }

      // Try cancel on SuvSign
      let cancelSucceeded = false;
      try {
        const { data: cancelData, error: cancelError } = await supabase.functions.invoke('suvsign-proxy', {
          body: {
            endpoint: `/documents/${freshContract.suvsign_document_id}/cancel`,
            method: 'POST',
          },
        });
        if (!cancelError && !(cancelData?.error)) {
          cancelSucceeded = true;
        }
      } catch (e) {
        console.warn('SuvSign cancel request failed:', e);
      }

      if (cancelSucceeded) {
        await updateContract(freshContract.id, { status: 'void', updated_at: new Date().toISOString() });
        toast.success('Contrato cancelado com sucesso.');
        onContractChange?.();
        return;
      }

      // Cancel failed — check real status
      try {
        const { data: docData } = await supabase.functions.invoke('suvsign-proxy', {
          body: { endpoint: `/documents/${freshContract.suvsign_document_id}`, method: 'GET' },
        });
        const suvStatus = docData?.status?.toLowerCase();

        if (suvStatus === 'signed' || suvStatus === 'completed') {
          const existingMetadata = (freshContract.metadata as any) || {};
          await updateContract(freshContract.id, {
            status: 'completed',
            metadata: { ...existingMetadata, cancelled_locally: true },
            updated_at: new Date().toISOString(),
          });
          toast.warning('Este contrato já foi assinado. Marcado como cancelado localmente.');
          onContractChange?.();
          return;
        }
        if (suvStatus === 'cancelled' || suvStatus === 'voided') {
          await updateContract(freshContract.id, { status: 'void', updated_at: new Date().toISOString() });
          toast.success('Contrato já estava cancelado na SuvSign. Status atualizado.');
          onContractChange?.();
          return;
        }
        // Active but cancel failed — update status
        const statusMap: Record<string, string> = { sent: 'sent', pending: 'sent', viewed: 'viewed', declined: 'declined', expired: 'expired' };
        const mapped = statusMap[suvStatus || ''];
        if (mapped) {
          await updateContract(freshContract.id, { status: mapped, updated_at: new Date().toISOString() });
          toast.warning(`Não foi possível cancelar. Status atualizado para "${mapped}".`);
          onContractChange?.();
          loadContracts();
          return;
        }
      } catch (e) {
        console.warn('Could not verify SuvSign status:', e);
      }

      // Fallback: delete locally
      await deleteContract(freshContract.id);
      toast.success('Contrato excluído localmente.');
      onContractChange?.();
    } catch (error: any) {
      console.error('Error dismissing contract:', error);
      toast.error(`Erro ao cancelar contrato: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setDismissing(false);
    }
  };

  const handleOpenSendDialog = (contract: any) => {
    setSelectedContract(contract);
    setIsSendDialogOpen(true);
  };

  const handleSendViaSuvSign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedContract) return;

    try {
      // Get signed URL for the document
      if (!selectedContract.document_id) {
        toast.error(t('contracts.documentRequired'));
        return;
      }

      const { data: document } = await supabase
        .from('documents')
        .select('storage_path')
        .eq('id', selectedContract.document_id)
        .single();

      if (!document?.storage_path) {
        toast.error(t('contracts.documentNotFound'));
        return;
      }

      // Get public URL for SuvSign to download
      const { data: urlData } = await supabase.storage
        .from('documents')
        .createSignedUrl(document.storage_path, 3600); // 1 hour expiry

      if (!urlData?.signedUrl) {
        toast.error(t('contracts.failedGenerateUrl'));
        return;
      }

      await sendToSuvSign({
        contractId: selectedContract.id,
        dealId,
        title: selectedContract.contract_name,
        fileUrl: urlData.signedUrl,
        recipientEmail: sendFormData.recipient_email,
        recipientName: sendFormData.recipient_name,
      });

      setIsSendDialogOpen(false);
    } catch (error) {
      console.error('Error sending via SuvSign:', error);
      toast.error(t('contracts.errorSending'));
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8">{t('contracts.loading')}</div>;
  }

  return (
    <div className="space-y-6">
      {/* WON read-only banner */}
      {isDealWon && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          Deal fechado. Contratos em modo somente leitura.
        </div>
      )}

      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">{t('contracts.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('contracts.description')}</p>
        </div>
        {!isDealWon && (
          <div className="flex gap-2">
            {isAdmin && (
              <Button variant="outline" onClick={() => setIsUploadDialogOpen(true)} disabled={hasActiveContract}>
                <Upload className="w-4 h-4 mr-2" />
                {t('contracts.uploadContract')}
              </Button>
            )}
            <Button 
              onClick={handleCreateContract}
              disabled={!contact || hasActiveContract}
              title={
                hasActiveContract 
                  ? 'Já existe um contrato ativo para este deal' 
                  : !contact 
                    ? t('contracts.waitingClientData') 
                    : undefined
              }
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('contracts.createContract')}
            </Button>
          </div>
        )}
      </div>

      {/* Contract Status Overview */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{contracts.filter(c => c.status === 'draft').length}</div>
            <div className="text-sm text-muted-foreground">{t('contracts.draft')}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{contracts.filter(c => c.status === 'sent' || c.status === 'viewed').length}</div>
            <div className="text-sm text-muted-foreground">{t('contracts.pending')}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">{contracts.filter(c => c.status === 'completed').length}</div>
            <div className="text-sm text-muted-foreground">{t('contracts.signed')}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-600">{contracts.filter(c => c.status === 'declined' || c.status === 'expired').length}</div>
            <div className="text-sm text-muted-foreground">{t('contracts.issues')}</div>
          </CardContent>
        </Card>
      </div>

      {/* Contracts List */}
      <div className="space-y-3">
        {contracts.map((contract) => {
          const StatusIcon = statusIcons[contract.status];
          const cancelledLocally = isCancelledLocally(contract);
          return (
            <Card key={contract.id} className={cancelledLocally ? 'opacity-70' : ''}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-5 h-5 text-muted-foreground" />
                      <h4 className="font-medium">{contract.contract_name}</h4>
                      {isAdmin && !isDealWon && !cancelledLocally && !((contract as any).integration_provider === 'suvsign' || (contract.metadata as any)?.source === 'template' || contract.suvsign_document_id) ? (
                        <Select
                          value={contract.status}
                          onValueChange={(newStatus) => handleUpdateStatus(contract.id, newStatus)}
                        >
                          <SelectTrigger className="w-[140px] h-7">
                            <div className="flex items-center gap-1">
                              <StatusIcon className="w-3 h-3" />
                              <SelectValue />
                            </div>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">{t('contracts.statusDraft')}</SelectItem>
                            <SelectItem value="sent">{t('contracts.statusSent')}</SelectItem>
                            <SelectItem value="viewed">{t('contracts.statusViewed')}</SelectItem>
                            <SelectItem value="completed">{t('contracts.statusCompleted')}</SelectItem>
                            <SelectItem value="declined">{t('contracts.statusDeclined')}</SelectItem>
                            <SelectItem value="issues">{t('contracts.statusIssues')}</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge className={statusColors[contract.status]}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {contract.status}
                        </Badge>
                      )}
                      {/* Cancelled locally tag */}
                      {cancelledLocally && (
                        <Badge className="bg-red-100 text-red-800 border-red-200">
                          <Ban className="w-3 h-3 mr-1" />
                          Contrato Cancelado
                        </Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      <div>
                        <span className="font-medium">{t('contracts.type')}:</span> {contract.contract_type}
                      </div>
                      <div>
                        <span className="font-medium">{t('contracts.created')}:</span> {safeFormat(new Date(contract.created_at), 'PP')}
                      </div>
                      {contract.sent_at && (
                        <div>
                          <span className="font-medium">{t('contracts.sent')}:</span> {safeFormat(new Date(contract.sent_at), 'PP')}
                        </div>
                      )}
                      {contract.signed_at && (
                        <div>
                          <span className="font-medium">{t('contracts.signed')}:</span> {safeFormat(new Date(contract.signed_at), 'PP')}
                        </div>
                      )}
                    </div>

                    {contract.notes && (
                      <p className="text-sm text-muted-foreground mt-2">{contract.notes}</p>
                    )}

                    {/* PandaDoc Integration Info */}
                    {contract.pandadoc_document_id && (
                      <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                        <div className="font-medium text-blue-900">{t('contracts.pandaDocDocument')}</div>
                        <div className="text-blue-700">ID: {contract.pandadoc_document_id}</div>
                        {contract.pandadoc_view_url && (
                          <a
                            href={contract.pandadoc_view_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {t('contracts.viewInPandaDoc')}
                          </a>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {/* Send via SuvSign button - only for manual drafts, not WON */}
                    {!isDealWon && contract.status === 'draft' && suvSignConfig?.is_active && !(contract.metadata as any)?.source && !cancelledLocally && (
                      <Button size="sm" onClick={() => handleOpenSendDialog(contract)}>
                        <Send className="w-4 h-4 mr-1" />
                        {t('contracts.sendViaSuvSign')}
                      </Button>
                    )}

                    {/* UNIFIED DISMISS button for all active statuses */}
                    {!isDealWon && !cancelledLocally && ['draft', 'sent', 'viewed', 'completed'].includes(contract.status) && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 w-8"
                              onClick={() => setContractToDismiss(contract)}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Cancelar Contrato</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}

                    {/* Show View/Download for completed contracts with signed file URL */}
                    {contract.status === 'completed' && contract.signed_file_url && (
                      <>
                        <Button size="sm" variant="outline" asChild>
                          <a href={contract.signed_file_url} target="_blank" rel="noopener noreferrer">
                            <Eye className="w-4 h-4 mr-1" />
                            {t('contracts.view')}
                          </a>
                        </Button>
                        <Button size="sm" variant="outline" asChild>
                          <a href={contract.signed_file_url} download target="_blank" rel="noopener noreferrer">
                            <Download className="w-4 h-4 mr-1" />
                            {t('contracts.download')}
                          </a>
                        </Button>
                      </>
                    )}
                    {/* Fallback for contract_url - use signed URL */}
                    {contract.contract_url && !contract.signed_file_url && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleDownload(contract)}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        {t('contracts.download')}
                      </Button>
                    )}
                    {contract.pandadoc_view_url && (
                      <Button size="sm" variant="outline" asChild>
                        <a href={contract.pandadoc_view_url} target="_blank" rel="noopener noreferrer">
                          <Eye className="w-4 h-4 mr-1" />
                          {t('contracts.view')}
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {contracts.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="mb-2">{t('contracts.noContractsYet')}</p>
              <p className="text-sm">{t('contracts.clickToCreate')}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Send via SuvSign Dialog */}
      <Dialog open={isSendDialogOpen} onOpenChange={setIsSendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('contracts.sendViaSuvSign')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSendViaSuvSign} className="space-y-4">
            <div>
              <Label htmlFor="recipient_name">{t('contracts.recipientName')} *</Label>
              <Input
                id="recipient_name"
                value={sendFormData.recipient_name}
                onChange={(e) => setSendFormData({ ...sendFormData, recipient_name: e.target.value })}
                placeholder={t('contracts.fullName')}
                required
              />
            </div>

            <div>
              <Label htmlFor="recipient_email">{t('contracts.recipientEmail')} *</Label>
              <Input
                id="recipient_email"
                type="email"
                value={sendFormData.recipient_email}
                onChange={(e) => setSendFormData({ ...sendFormData, recipient_email: e.target.value })}
                placeholder="email@example.com"
                required
              />
            </div>

            <div className="bg-blue-50 p-3 rounded text-sm text-blue-800">
              <p><strong>{t('contracts.contract')}:</strong> {selectedContract?.contract_name}</p>
              <p className="mt-1">
                {t('contracts.contractInfo')}
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsSendDialogOpen(false)}>
                {t('contracts.cancel')}
              </Button>
              <Button type="submit" disabled={suvSignLoading}>
                {suvSignLoading ? t('contracts.sending') : t('contracts.sendViaSuvSign')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* UNIFIED Dismiss Contract Confirmation */}
      <AlertDialog open={!!contractToDismiss} onOpenChange={(open) => !open && setContractToDismiss(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-destructive" />
              Cancelar Contrato
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 text-left">
              <p className="text-foreground font-medium">
                Deseja cancelar este contrato? Se já enviado, tentaremos cancelar na plataforma de assinatura.
              </p>
              {contractToDismiss?.contract_name && (
                <p className="text-sm text-muted-foreground">
                  Contrato: <strong>{contractToDismiss.contract_name}</strong>
                </p>
              )}
              {(contractToDismiss?.suvsign_document_id || (contractToDismiss?.metadata as any)?.source === 'template') && (
                <div className="bg-muted border border-border rounded-lg p-3">
                  <p className="text-sm text-muted-foreground">
                    ⚠️ <strong className="text-foreground">O sistema tentará cancelar o contrato também na SuvSign.</strong>
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setContractToDismiss(null)} disabled={dismissing}>
              Voltar
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={dismissing}
              onClick={() => {
                handleDismissContract(contractToDismiss);
                setContractToDismiss(null);
              }}
            >
              {dismissing ? 'Processando...' : 'Confirmar Cancelamento'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upload Contract Dialog - Admin Only */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('contracts.uploadContract')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUploadContract} className="space-y-4">
            <div>
              <Label>{t('contracts.selectFile')}</Label>
              <Input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                required
              />
            </div>

            <div>
              <Label>{t('contracts.contractName')}</Label>
              <Input
                value={uploadFormData.contract_name}
                onChange={(e) => setUploadFormData({...uploadFormData, contract_name: e.target.value})}
                placeholder={t('contracts.contractNamePlaceholder')}
              />
            </div>

            <div>
              <Label>{t('contracts.initialStatus')}</Label>
              <Select
                value={uploadFormData.status}
                onValueChange={(v) => setUploadFormData({...uploadFormData, status: v as 'draft' | 'sent' | 'completed' | 'issues'})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">{t('contracts.statusDraft')}</SelectItem>
                  <SelectItem value="sent">{t('contracts.statusSent')}</SelectItem>
                  <SelectItem value="completed">{t('contracts.statusCompleted')}</SelectItem>
                  <SelectItem value="issues">{t('contracts.statusIssues')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t('contracts.notes')}</Label>
              <Textarea
                value={uploadFormData.notes}
                onChange={(e) => setUploadFormData({...uploadFormData, notes: e.target.value})}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsUploadDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={uploading || !uploadFile}>
                {uploading ? t('contracts.uploading') : t('contracts.upload')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de confirmação de redirecionamento */}
      <AlertDialog open={showRedirectConfirm} onOpenChange={setShowRedirectConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Criar Contrato</AlertDialogTitle>
            <AlertDialogDescription>
              Você será redirecionado para a plataforma de assinatura (SuvSign) em uma nova aba para finalizar a criação do contrato. Deseja continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingRedirectData(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCreateContract}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
